import {LOCALE_CATEGORIES} from "@/assets/locales/categories";

import {AccountRoute} from "@/domains/-account/routes";
import {AdminListRoute} from "@/domains/-admin/-list/routes";
import {getAfterSignUpRouteRecord} from "@/domains/-authentication/-afterSignUp/routes";
import {getConfirmAccountRouteRecord} from "@/domains/-authentication/-confirmAccount/routes";
import {getResetPasswordRouteRecord} from "@/domains/-authentication/-resetPassword/routes";
import {getResetPasswordFormRouteRecord} from "@/domains/-authentication/-resetPasswordForm/routes";
import {getSignInRouteLocation, getSignInRouteRecord} from "@/domains/-authentication/-signIn/routes";

import {getSignUpRouteRecord} from "@/domains/-authentication/-signUp/routes";
import { BootcampCreateRoute } from "@/domains/-bootcamp/-create/routes.ts";
import { BootcampListRoute } from "@/domains/-bootcamp/-list/routes.ts";
import { BootcampUpdateRoute } from "@/domains/-bootcamp/-update/routes.ts";

import {CoacheeListRoute, getClientListRouteLocation} from "@/domains/-coachee/-list/routes";
import {getCoacheeJourneyRouteRecord, getCoacheeOverviewRouteRecord} from "@/domains/-coachee/-overview/routes.ts";
import {getEventsRouteRecord, getGlossaryRouteRecord, getResourcesRouteRecord} from "@/domains/-community/routes";
import {ContactUsRoute} from "@/domains/-contact-us/view";

import {getLegalTermsRouteRecord} from "@/domains/-contract/-legalTerms/routes";
import {getTermsOfServiceRouteRecord} from "@/domains/-contract/-termsOfService/routes";
import {getCollectiveToolsLandingRouteRecord, getCollectiveToolsListRouteRecord} from "@/domains/-collective-tools/routes.ts";
import { getDebriefHelperRouteRecord } from "@/domains/-journey/-debrief/routes";
import {getSynthesisRouteRecord} from "@/domains/-journey/-synthesis/routes";
import {WorkspacePersonMe} from "@/domains/-session/models/WorkspacePersonMe.ts";
import {useSessionStore} from "@/domains/-session/stores/useSessionStore";
import {customAsyncComponent} from "@/functions/customAsyncComponent";
import {i18n, loadLocales} from '@/i18n'
import {WorkspaceId} from "@/sdk.ts";
import {createRouter, createWebHistory, RouteLocationNormalized, RouteRecordRaw} from "vue-router";
import {useToast} from "vue-toastification";


const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior (to, from, savedPosition) {
    if(to.hash === '')
      setTimeout(() => {
        const container = document.querySelector('.ods-content-container > div')
        if(container)
          container.scrollTop = 0
      }, 500)
  },
  routes: [
    getSignInRouteRecord(),
    getSignUpRouteRecord(),
    getAfterSignUpRouteRecord(),
    getConfirmAccountRouteRecord(),
    getResetPasswordRouteRecord(),
    getResetPasswordFormRouteRecord(),
    getLegalTermsRouteRecord(),
    getTermsOfServiceRouteRecord(),
    CoacheeListRoute.record(),
    getCoacheeOverviewRouteRecord(),
    getCoacheeJourneyRouteRecord(),
    getSynthesisRouteRecord(),
    getDebriefHelperRouteRecord(),
    AccountRoute.record(),
    getCollectiveToolsLandingRouteRecord(),
    getCollectiveToolsListRouteRecord(),
    getEventsRouteRecord(),
    getResourcesRouteRecord(),
    getGlossaryRouteRecord(),
    AdminListRoute.record(),
    BootcampListRoute.record(),
    BootcampCreateRoute.record(),
    BootcampUpdateRoute.record(),
    ContactUsRoute.record(),
    {
      name: 'popup-temp',
      path: '/workspace/:workspaceId/popup-temp',
      component: customAsyncComponent(async () => import('./PopupTemp.vue')),
      meta: {
        requiresAuth: true
      }
    },
    {
      name: 'home',
      path: '/',
      component: () => null,
      beforeEnter: async to => {
        const session = useSessionStore()
        await session.loadUser()
        if (session.workspaces.length === 0) {
          const toast = useToast()
          toast.error(i18n.global.t('errors.sign_in.error_no_workspace'))
          return getSignInRouteLocation()
        }
        return getClientListRouteLocation(session.workspaces[0].id)
      },
      meta: {
        requiresAuth: true
      }
    }
  ],
})

export function isExactActiveRoute(routeName: string): boolean {
  return router.currentRoute.value.name === routeName
}

export function isActiveRoute(routeName: string): boolean {
  return (
    isExactActiveRoute(routeName)
    || router.currentRoute.value.matched.some(item => item.name === routeName)
  )
}

function roleRedirect(me: WorkspacePersonMe, reason: string, workspaceId?: WorkspaceId) {
  if (!!me.coach || !!me.extern) {
    console.log(`redirection to coachee list view. Reason : ${reason} on workspace[${workspaceId ?? me.workspace.id}]`)
    return CoacheeListRoute.location(me.workspace.id);
  }

  if (!!me.admin) {
    console.log(`redirection to staff list view. Reason : ${reason} on workspace[${workspaceId ?? me.workspace.id}]`)
    return AdminListRoute.location(me.workspace.id);
  }

  if (!!me.coachee) {
    console.log(`redirection to profile view. Reason : ${reason} on workspace[${workspaceId ?? me.workspace.id}]`);
    return AccountRoute.location({workspaceId: me.workspace.id});
  }

  throw new Error(`no valid role found !`)
}


router.beforeEach(async (to: RouteLocationNormalized) => {
  await loadLocales(LOCALE_CATEGORIES.ERRORS)

  if (Object.hasOwn(to.query, 'id'))
    return {name: 'new_account_system'}

  const requiresAuth = to.matched.some((record: RouteRecordRaw) => (record.meta && Object.hasOwn(record.meta, 'requiresAuth')) ?? false)
  if (requiresAuth) {
    // Update the session (getMe if not already done)
    const session = useSessionStore()
    await session.loadUser('forceMe' in to.meta && !!to.meta.forceMe)

    // Redirect unauthenticated users to `sign-in`
    if (!session.loaded)
      return {name: "sign_in", query: {redirect: to.fullPath}};
  }

  const requiresCoach = to.matched.some((record: RouteRecordRaw) =>
    record.meta
    && 'requiresCoach' in record.meta
    && record.meta.requiresCoach
  )
  const requiresAdmin = to.matched.some((record: RouteRecordRaw) =>
    record.meta
    && 'requiresAdmin' in record.meta
    && record.meta.requiresAdmin
  )

  if (requiresCoach || requiresAdmin) {
    const session = useSessionStore();
    await session.loadUser();
    const workspaceId = 'workspaceId' in to.params && typeof to.params.workspaceId === "string"
      ? parseInt(to.params.workspaceId, 10)
      : undefined;
    session.wsp = session.users.find(user => user.workspace.id === workspaceId);
    const me = session.wsp;

    if (!me) {
      const suitableMe = session.users.find(user =>
        !!user.admin || !!user.coach || !!user.extern || !!user.coachee
      )

      // This should not happen, possible api issue.
      if (!suitableMe) throw new Error(`no suitable workspace`);

      return roleRedirect(suitableMe, 'no role', workspaceId)
    }
    if (requiresAdmin && !me.admin) return roleRedirect(me, 'not admin');
    if (requiresCoach && !me.coach && !me.extern) return roleRedirect(me, 'not coach or extern');
  }

  return;
})

router.beforeResolve(async (to: RouteLocationNormalized) => {
  await loadLocales(LOCALE_CATEGORIES.LEXICON)
  if ('hasNav' in to.meta && to.meta.hasNav === true) {
    await loadLocales(LOCALE_CATEGORIES.NAVIGATION)
  }
  if ('localeCategories' in to.meta) {
    await loadLocales(...(to.meta.localeCategories as LOCALE_CATEGORIES[]))
  }
})

export {router}
export default router